import { lazy, Suspense } from "react";
// router
import { createBrowserRouter } from "react-router-dom";
// layouts
import MainLayout from "../components/layout/MainLayout";
import ProtectedRoutes from "../components/layout/ProtectedRoutes";

// pages
import Home from "../pages/home/Home";
import NotFound from "../pages/not-found/NotFound";
import AllowedTo from "../components/layout/AllowedTo";
import ThreeDotsLoader from "../components/loader/ThreeDotsLoader";
import { PermissionGuard } from "../components/permission-guard/permission-guard";
import { PERMISSIONS } from "../data/permissions";
// lazy pages
const Login = lazy(() => import("../pages/login/Login"));
// categories
const Categories = lazy(() => import("../pages/categories/Categories"));
const AddCategory = lazy(() => import("../pages/categories/AddCategory"));
const EditCategory = lazy(() => import("../pages/categories/EditCategory"));
// payment methods
const PaymentMethods = lazy(() => import("../pages/categories/PaymentMethods"));
// users
const Users = lazy(() => import("../pages/users/Users"));
const AddUser = lazy(() => import("../pages/users/AddUser"));
const EditUser = lazy(() => import("../pages/users/EditUser"));
// exams
const Exams = lazy(() => import("../pages/exams/Exams"));
const AddExam = lazy(() => import("../pages/exams/AddExam"));
const EditExam = lazy(() => import("../pages/exams/EditExam"));
// questions
const Questions = lazy(() => import("../pages/questions/Questions"));
// transactions
const Transactions = lazy(() => import("../pages/transactions/Transactions"));
// user exam purchases
const UserExamPurchases = lazy(
  () => import("../pages/user-exam-purchases/UserExamPurchases")
);
// notifications
const PushNotification = lazy(
  () => import("../pages/notifications/PushNotification")
);
// point packages
const PointPackages = lazy(
  () => import("../pages/point-packages/PointPackages")
);
const AddPointPackage = lazy(
  () => import("../pages/point-packages/AddPointPackage")
);
const EditPointPackage = lazy(
  () => import("../pages/point-packages/EditPointPackage")
);
// banners
const Banners = lazy(() => import("../pages/banners/Banners"));
const AddBanner = lazy(() => import("../pages/banners/AddBanner"));
const EditBanner = lazy(() => import("../pages/banners/EditBanner"));
// prepaid cards
const PrepaidCards = lazy(() => import("../pages/prepaid-cards/PrepaidCards"));
// settings
const Settings = lazy(() => import("../pages/settings/Settings"));

// all routes
const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProtectedRoutes>
        <MainLayout />
      </ProtectedRoutes>
    ),
    children: [
      {
        path: "",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_STATISTICS_PROCESSES_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <Home />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // users
      {
        path: "users",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.POST_USERS]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <Users />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.POST_USERS]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <AddUser />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ":userId",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.PUT_USERS_BY_ID]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <EditUser />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // categories
      {
        path: "categories",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_CATEGORIES_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <Categories />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.POST_CATEGORIES]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <AddCategory />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ":categoryId",
            // PUT_CATEGORIES_BY_ID
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.PUT_CATEGORIES_BY_ID]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <EditCategory />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // exams
      {
        path: "exams",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_EXAMS_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <Exams />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.POST_EXAMS_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <AddExam />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ":examId",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.PUT_EXAMS_BY_ID_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <EditExam />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // point-packages
      {
        path: "points-packages",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_POINT_PACKAGES_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <PointPackages />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.POST_POINT_PACKAGES]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <AddPointPackage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ":packageId",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.PUT_POINT_PACKAGES_BY_ID]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <EditPointPackage />{" "}
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // questions
      {
        path: "questions",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_QUESTIONS_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <Questions />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // transactions
      {
        path: "transactions",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_WALLETS_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <Transactions />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // user exam purchases
      {
        path: "user-exam-purchases",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_USER_EXAM_PURCHASES_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <UserExamPurchases />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // notifications
      {
        path: "push-notifications",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.POST_FCM_NOTIFICATIONS]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <PushNotification />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // banners
      {
        path: "banners",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_BANNERS_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  {" "}
                  <Banners />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.POST_BANNERS]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <AddBanner />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ":bannerId",
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.PUT_BANNERS_BY_ID]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <EditBanner />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // prepaid-cards
      {
        path: "prepaid-cards",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_PREPAID_CARDS]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <PrepaidCards />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
      // settings
      {
        path: "settings",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ThreeDotsLoader />}>
                <PermissionGuard
                  permissions={[PERMISSIONS.GET_SETTINGS_ADMIN]}
                  requireAll={false}
                  fallBackType="unauthorized"
                >
                  <Settings />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);
export default router;
